import Grid from '@mui/material/Grid';
import PropTypes from 'prop-types';
import { Dialog, DialogActions, DialogContent, Fab } from '@mui/material';
import { Add } from '@mui/icons-material';
import Divider from '@mui/material/Divider';
import Tooltip from '@mui/material/Tooltip';
import { useIntl } from 'react-intl';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContentText from '@mui/material/DialogContentText';
import { useState } from 'react';
import Icon from '@mui/material/Icon';
import MDButton from 'components/MDButton';
import MDBox from 'components/MDBox';
import { GridCard } from 'components/Cards/GridCards/components/GridCard';
import MDTypography from 'components/MDTypography';

export function GridCards({
  items = [],
  onCreate,
  onUpdate,
  onDelete,
  onSelect,
  onPlay,
  canEdit = true,
  defaultIcon = 'category',
  emptyListLabel = 'Aucun élément dans la liste',
  ...rest
}) {
  const { formatMessage: msg } = useIntl();
  const [confirmDeleteDialogOpen, setConfirmDeleteDialogOpen] = useState(false);
  const [currentDeletedItemId, setCurrentDeletedItemId] = useState(null);

  const handleDeleteBtnClick = (id) => {
    setCurrentDeletedItemId(id);
    setConfirmDeleteDialogOpen(true);
  };

  const handleCancelDialogClose = () => {
    setConfirmDeleteDialogOpen(null);
    setConfirmDeleteDialogOpen(false);
  };

  const handleConfirmDeleteBtnClick = () => {
    onDelete(currentDeletedItemId);
    handleCancelDialogClose();
  };

  return (
    <MDBox sx={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
      {items?.length === 0 ? (
        <MDBox
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            flex: 1,
            color: 'lightgray',
            py: 5
          }}
        >
          <Icon sx={{ fontSize: '200px!important' }}>{defaultIcon}</Icon>
          <MDTypography variant="h3" sx={{ mt: 2, color: 'lightgray' }}>
            {emptyListLabel}
          </MDTypography>
        </MDBox>
      ) : (
        <Grid
          container
          direction="row"
          rowSpacing={7}
          columnSpacing={4}
          {...rest}
          id="gridContainer"
          mb={2}
        >
          {items.map((item) => (
            <Grid id={`gridItem_${item.id}`} key={item.id}>
              <GridCard
                key={item.id}
                item={item}
                defaultIcon={defaultIcon}
                onCreateBtnClick={() => onCreate()}
                onUpdateBtnClick={() => onUpdate(item.id)}
                onDeleteBtnClick={() => handleDeleteBtnClick(item.id)}
                onSelectBtnClick={() => onSelect(item.id)}
                onPlayBtnClick={onPlay ? () => onPlay(item.id) : null}
                canEdit={canEdit}
              />
            </Grid>
          ))}
        </Grid>
      )}
      <Divider sx={{ marginTop: 'auto' }} />
      {canEdit && (
        <MDBox id="buttonBar" sx={{ display: 'flex' }}>
          <Tooltip title={msg({ id: 'tooltip.create' })}>
            <Fab
              variant="extended"
              color="primary"
              sx={{ marginLeft: 'auto', maxWidth: '200px' }}
              onClick={() => onCreate()}
            >
              <Add fontSize="large" sx={{ mr: 1 }} />
              {msg({ id: 'button.create' })}
            </Fab>
          </Tooltip>
        </MDBox>
      )}
      <Dialog open={confirmDeleteDialogOpen} onClose={handleCancelDialogClose}>
        <DialogTitle>{msg({ id: 'dialog.delete.confirm.title' })}</DialogTitle>
        <DialogContent>
          <DialogContentText>{msg({ id: 'dialog.delete.confirm.message' })}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <MDButton onClick={handleCancelDialogClose} color="secondary">
            {msg({ id: 'button.cancel' })}
          </MDButton>
          <MDButton onClick={handleConfirmDeleteBtnClick} color="error">
            {msg({ id: 'button.delete' })}
          </MDButton>
        </DialogActions>
      </Dialog>
    </MDBox>
  );
}

GridCards.propTypes = {
  defaultIcon: PropTypes.string,
  emptyListLabel: PropTypes.string,
  items: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      label: PropTypes.string.isRequired,
      icon: PropTypes.string,
      color: PropTypes.string
    })
  ).isRequired,
  onCreate: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onSelect: PropTypes.func.isRequired,
  onUpdate: PropTypes.func.isRequired,
  canEdit: PropTypes.bool,
  onPlay: PropTypes.func
};
